import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import ReplacementPartsFields from '../../components/printer-maintenances/ReplacementPartsFields';
import TimeInputWithBlur from '../../components/TimeInputWithBlur';
import { apiDateTimeToLocalInput } from '../../helpers/formatters';
import { sortPrintersByName } from '../../helpers/printers';
import {
    buildMaintenancePayload,
    DEFAULT_MAINTENANCE_DURATION,
    durationToMinutes,
    emptyPartRow,
    MAINTENANCE_TYPES,
    numericInputProps,
} from '../../helpers/printerMaintenances';

function RegisterPrinterMaintenancePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Só o valor que chegou do painel é estimado. O que o usuário digitar depois
    // é leitura dele, então o aviso sai de cena.
    const suggestedHoursCount = searchParams.get('printer_hours_count') || '';
    const [printers, setPrinters] = useState([]);
    const [form, setForm] = useState({
        printer_uuid: searchParams.get('printer_uuid') || '',
        // Quase toda manutenção é registrada logo depois de ser feita, então o
        // agora é o palpite certo; quem registra atrasado só corrige o campo.
        occurred_at: apiDateTimeToLocalInput(new Date().toISOString()) || '',
        // O painel de próximas manutenções chega aqui já dizendo qual tarefa está
        // vencendo, para o usuário não redigitar o que o sistema já sabe.
        type: searchParams.get('type') || 'manutencao_preventiva',
        // Manutenção preventiva não nasce de um problema, então a descrição vem
        // com um ponto só para satisfazer o campo obrigatório: o que interessa é
        // a tarefa executada, que entra em "o que foi feito".
        problem_description: searchParams.get('problem_description') || '',
        analysis_duration: DEFAULT_MAINTENANCE_DURATION,
        performed_actions: searchParams.get('performed_actions') || '',
        // Vem do painel já somando a contagem da última manutenção da tarefa com
        // as horas impressas desde então, para o usuário só conferir.
        printer_hours_count: suggestedHoursCount,
        correction_duration: DEFAULT_MAINTENANCE_DURATION,
    });
    const [replacementParts, setReplacementParts] = useState([emptyPartRow()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                setPrinters(sortPrintersByName(response.data.printers || []));
            })
            .catch(() => {
                setDidNotLoad(true);
            });
    }, []);

    const updateForm = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'printer_uuid') {
            setReplacementParts((rows) => rows.map((row) => ({ ...row, stock_part_uuid: '' })));
        }
    };

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const nextErrors = { has_errors: false };
        if (!form.printer_uuid) {
            nextErrors.printer_uuid = 'Selecione a impressora.';
        }
        if (!form.problem_description.trim()) {
            nextErrors.problem_description = 'Informe a descrição do problema.';
        }
        if (form.analysis_duration.replace(/\D/g, '') && durationToMinutes(form.analysis_duration) === null) {
            nextErrors.analysis_duration = 'Minutos inválidos (use até 59).';
        }
        if (form.correction_duration.replace(/\D/g, '') && durationToMinutes(form.correction_duration) === null) {
            nextErrors.correction_duration = 'Minutos inválidos (use até 59).';
        }

        if (Object.keys(nextErrors).length > 1) {
            nextErrors.has_errors = true;
            setResultErrors(nextErrors);
            return;
        }

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        api.post('printer-maintenances', buildMaintenancePayload(form, replacementParts))
            .then(() => {
                navigate('/admin/printer-maintenances');
            })
            .catch((error) => {
                const data = error.response?.data || {};
                setResultErrors({
                    ...(data.errors || {}),
                    has_errors: true,
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/printer-maintenances')} to="/admin/printer-maintenances">
                    Manutenções
                </Link>
                <Typography color="text.primary">Adicionando manutenção</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Adicionar Nova Manutenção</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="printer-label">Impressora</InputLabel>
                            <Select
                                labelId="printer-label"
                                label="Impressora"
                                value={form.printer_uuid}
                                onChange={(event) => updateForm('printer_uuid', event.target.value)}
                            >
                                {printers.map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('printer_uuid')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Data da manutenção"
                            fullWidth
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                            value={form.occurred_at}
                            onChange={(event) => updateForm('occurred_at', event.target.value)}
                            helperText="Ajuste se a manutenção foi feita em outro momento"
                        />
                        {renderFieldError('occurred_at')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="type-label">Tipo</InputLabel>
                            <Select
                                labelId="type-label"
                                label="Tipo"
                                value={form.type}
                                onChange={(event) => updateForm('type', event.target.value)}
                            >
                                {MAINTENANCE_TYPES.map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('type')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Contagem de horas da impressora"
                            fullWidth
                            type="number"
                            inputProps={numericInputProps(0)}
                            value={form.printer_hours_count}
                            onChange={(event) => updateForm('printer_hours_count', event.target.value)}
                            helperText={suggestedHoursCount
                                ? 'Contagem estimada com base nos registros de impressões, ajuste se souber a real'
                                : undefined}
                        />
                        {renderFieldError('printer_hours_count')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Descrição do problema"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.problem_description}
                            onChange={(event) => updateForm('problem_description', event.target.value)}
                        />
                        {renderFieldError('problem_description')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TimeInputWithBlur
                            label="Tempo de análise"
                            value={form.analysis_duration}
                            onChange={(event) => updateForm('analysis_duration', event.target.value)}
                        />
                        {renderFieldError('analysis_duration')}
                        {renderFieldError('analysis_duration_minutes')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TimeInputWithBlur
                            label="Tempo de correção"
                            value={form.correction_duration}
                            onChange={(event) => updateForm('correction_duration', event.target.value)}
                        />
                        {renderFieldError('correction_duration')}
                        {renderFieldError('correction_duration_minutes')}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="O que foi feito"
                            fullWidth
                            multiline
                            minRows={3}
                            value={form.performed_actions}
                            onChange={(event) => updateForm('performed_actions', event.target.value)}
                        />
                        {renderFieldError('performed_actions')}
                    </Grid>
                    <Grid item xs={12}>
                        <ReplacementPartsFields
                            value={replacementParts}
                            onChange={setReplacementParts}
                            printer={printers.find((item) => item.id === form.printer_uuid) || null}
                        />
                        {renderFieldError('replacement_parts')}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Cadastrando...' : 'Cadastrar Manutenção'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export default RegisterPrinterMaintenancePage;
